import {Component, OnInit, NgZone, OnDestroy, ViewChild, ElementRef} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {FontAwesomeService} from '@services/font-awesome.service';
import {UserService} from '@services/user.service';
import {FormBuilder, FormControl, FormGroup, Validators, AbstractControl} from '@angular/forms';
import {DialogSharedComponent} from '@components/dialog-shared/dialog-shared.component';
import {ShipmentService} from '@services/shipment.service';
import {InitInformation} from '@models/init-information.interface';
import {MapsAPILoader} from '@agm/core';
import {MatDialog} from '@angular/material';
import {AppState, selectFooterAction} from '@store/reducers';
import {Store} from '@ngrx/store';
import {AddAddress, EditAddress} from '@store/actions';
import {FooterActionEnum} from '@models/footer-action.enum';
import {Subject} from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import {FormUtilService} from '@services/form-util.service';
import {TranslateService} from '@ngx-translate/core';
import {takeUntil} from 'rxjs/operators';
import {AddressService} from '@services/address.service';
import 'rxjs/add/operator/map';

declare var google: any;

@Component({
  selector: 'app-address-edit',
  templateUrl: './address-edit.component.html',
  styleUrls: ['./address-edit.component.scss']
})
export class AddressEditComponent implements OnInit, OnDestroy {
  form: FormGroup;
  submitted = false;
  geocoder: any;
  possibleCountries: Array<{ value: string; label: string }>;
  completeForm: any;
  private unsubscribe: Subject<void> = new Subject();
  autocompleteAddresses: Array<{label: string, address: string, postcode: string, city: string}>;

  @ViewChild('search') search: ElementRef;

  constructor(
    private activateRoute: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private _fb: FormBuilder,
    private shipmentService: ShipmentService,
    private mapsAPILoader: MapsAPILoader,
    private ngZone: NgZone,
    public dialog: MatDialog,
    public faService: FontAwesomeService,
    public translate: TranslateService,
    private formUtilService: FormUtilService,
    private store: Store<AppState>,
    private addressService: AddressService
  ) {
  }

  ngOnInit() {
    this.initializeForm();

    this.shipmentService.getInitialData()
      .subscribe((data: InitInformation) => {
        this.possibleCountries = this.shipmentService.possibleCountries;
        // this.autocompleteform();
      });

    this.activateRoute.params
      .takeUntil(this.unsubscribe)
      .subscribe(({id}) => {
        if (id) {
          this.getAddressBook(id);
        }
      });

    this.form.get('company').valueChanges
      .takeUntil(this.unsubscribe)
      .subscribe(value => {
        const controls = ['name', 'surname', 'email'];
        if (+value) {
          this.changeValidator(controls, true);
          this.form.addControl('company_name', new FormControl('', Validators.required));
        } else {
          this.changeValidator(controls);
          this.form.removeControl('company_name');
        }
      });

    this.store.select(selectFooterAction)
      .takeUntil(this.unsubscribe)
      .subscribe(action => {
        if (action === FooterActionEnum.SAVE_ADDRESS) {
          this.save();
        }
      });

    // Address autocomplete
    this.form.get('address').valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .debounceTime(500)
      .distinctUntilChanged()
      .subscribe((query) => {
        this.autocompleteAddresses = [];
        if (query.length > 3) {
          const countryCode = this.form.get('country_code').value;
          if (this.addressService.validCountryCodes.includes(countryCode)) {
            this.addressService.autocomplete(countryCode, query)
              .pipe(takeUntil(this.unsubscribe))
              .subscribe(autocomplete => {
                this.autocompleteAddresses = autocomplete;
              });
          }
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private changeValidator(controls: string[], clearValidator: boolean = false): void {
    controls.forEach((name) => {
      const control = this.form.get(name);
      const invalid = control.invalid;
      clearValidator
        ? control.clearValidators()
        : control.setValidators(Validators.required);
      if (control.dirty && invalid) {
        control.updateValueAndValidity();
      }
    });
  }

  public save(): void {
    if (this.form.invalid) {
      this.formUtilService.markAsTouchedDeep(this.form);
      // this.dialog.open(DialogSharedComponent, {width: '450px', data: {errorType: 'null', removeShow: false, showCancel: false, showOk: true, header: 'error.validation.general'}});
      return;
    }

    const data: any = {...this.form.getRawValue(), type: '0'};
    if (this.form.contains('id')) {
      this.store.dispatch(new EditAddress(data));
    } else {
      this.store.dispatch(new AddAddress(data));
    }
  }

  /**
  autocompleteform() {
    this.mapsAPILoader.load().then(() => {
      this.geocoder = new google.maps.Geocoder();
      this.completeForm = new google.maps.places.Autocomplete(this.search.nativeElement, {
        types: ['geocode'],
        componentRestrictions: {country: this.possibleCountries.map(({value}) => value)}
      });

      google.maps.event.addListener(this.completeForm, 'place_changed', () => {
        this.ngZone.run(() => {
          this.addressSet(this.completeForm);
        });
      });
    });
  }

  addressSet(autocompleteFrom) {
    const tmpPlace = autocompleteFrom.getPlace();
    const place = this.getAddressComponents(tmpPlace.address_components);
    this.form.get('address').setValue(this.addressCompile(place));
    this.form.get('zip').setValue(place.postal_code.long);
    this.form.get('city').setValue(place.locality.long ? place.locality.long : place.postal_town.long);
    this.form.get('country').setValue(place.country.long);
    this.form.get('country_code').setValue(place.country.short);
  }

  addressCompile(place) {
    if (place.route.short) {
      if (place.street_number.short) {
        return place.route.short + ', ' + place.street_number.short;
      } else {
        return place.route.short;
      }
    } else {
      return '';
    }
  }

  getAddressComponents(place: Array<any>) {
    const componentForm = {
      street_number: {'long': '', 'short': ''},
      route: {'long': '', 'short': ''},
      locality: {'long': '', 'short': ''},
      postal_town: {'long': '', 'short': ''},
      administrative_area_level_1: {'long': '', 'short': ''},
      country: {'long': '', 'short': ''},
      postal_code: {'long': '', 'short': ''}
    };
    // Get each component of the address from the place details
    // and fill the corresponding field on the form.
    for (let i = 0; i < place.length; i++) {
      const addressType = place[i].types[0];
      if (componentForm[addressType]) {
        componentForm[addressType]['long'] = place[i]['long_name'];
        componentForm[addressType]['short'] = place[i]['short_name'];
      }
    }
    return componentForm;
  }
   */


  private getAddressBook(id) {
    this.userService.getAddressBook(id)
      .subscribe((data) => {
        if (data) {
          this.form.addControl('id', new FormControl());
          this.form.patchValue(data);
        }
      });
  }

  private initializeForm() {
    this.form = this._fb.group({
      alias: ['', Validators.required],
      company_name: [''],
      company: [1, Validators.required],
      address: ['', Validators.required],
      zip: ['', Validators.required],
      city: ['', Validators.required],
      country: ['Sweden', Validators.required],
      country_code: ['SE', Validators.required],
      name: [''],
      surname: [''],
      phone: ['', Validators.required],
      email: [''],
      autocomplete: [''],
      instructions: [''],
      door_code: [''],
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/address-book/address-edit/address-edit.component.ts